import { forwardRef } from "react";
import type { ButtonHTMLAttributes, ReactNode } from "react";
import { cn } from "../../lib/utils";

export interface IconButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "aria-label"> {
  /** Required: icon-only buttons must have an accessible name. */
  label: string;
  icon: ReactNode;
  variant?: "ghost" | "surface" | "primary";
  size?: "md" | "lg";
}

const VARIANTS = {
  ghost: "text-foreground hover:bg-surface",
  surface: "bg-surface text-foreground hover:bg-surface-raised",
  primary: "bg-primary text-primary-foreground hover:bg-primary/90",
} as const;

/** Round, 44px icon-only button with an aria-label and focus ring. */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ label, icon, variant = "ghost", size = "md", className, type = "button", ...rest }, ref) => (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      title={label}
      className={cn(
        "inline-flex items-center justify-center rounded-full transition-colors shrink-0",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        "active:scale-95 disabled:opacity-50 disabled:pointer-events-none",
        size === "md" ? "w-11 h-11" : "w-12 h-12",
        VARIANTS[variant],
        className
      )}
      {...rest}
    >
      <span aria-hidden="true" className="inline-flex">
        {icon}
      </span>
    </button>
  )
);
IconButton.displayName = "IconButton";

export default IconButton;
